import classnames from 'classnames';
import dynamic from 'next/dynamic';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import { frontConfig } from '../../config';
import type { WrappedEditorProps } from '../../editor';
import type { GraphEditorInstanceRef } from '../../editor/editor-model';
import type { EjsExtraProps } from '../../editor/editorjs/Editorjs';
import { serializedToFullHTML } from '../../editor/editorjs/editorjs-utils';
import { useWebsite } from '../../hooks/useWebsite';
import {
  type SerializedValue,
  type SerializeValue,
} from '../../thread/ThreadMessage/useSerializeValue';
import type { UseGraphFieldArgument } from './useGraphField';
import { useGraphField } from './useGraphField';

interface EditorFieldExtraProps extends EjsExtraProps {
  initialValue: SerializedValue;
  serializeValue: SerializeValue;
  editorRef?: GraphEditorInstanceRef;
}

const DEFAULT_EDITOR_CONTENT = '<p></p>';

const getValidHiddenEmbeds = (elements: string[]) => (elements?.length > 0 ? elements : []);

const WrappedEditor = dynamic<WrappedEditorProps>(() => import('../../editor'), {
  ssr: false,
});

// import { Editorjs } from '../../editor/editorjs/Editorjs';
const Editorjs = dynamic(
  () => import('../../editor/editorjs/Editorjs').then(exports => exports.Editorjs),
  { ssr: false },
);

export const EditorField: FC<
  UseGraphFieldArgument<SerializedValue> & Partial<WrappedEditorProps> & EditorFieldExtraProps
> = ({
  id,
  name,
  validate,
  onFocus: propsOnFocus,
  onBlur: propsOnBlur,
  initialValue,
  serializeValue,
  hasExternalSend,
  editorRef,
  onCancelAnswer,
  hasInitialValue,
  hideIfEmpty,
  noBorder,
  withAvatar,
  contentType,
  ...props
}) => {
  const form = useForm();
  const website = useWebsite();

  const options = useMemo<UseGraphFieldArgument<SerializedValue>>(
    () => ({
      name,
      validate,
      config: { initialValue },
    }),
    [initialValue, name, validate],
  );
  const {
    input: { value: content, onFocus, onBlur },
    computedError,
  } = useGraphField<SerializedValue, HTMLInputElement>(options);
  const {
    input: { value: hiddenEmbeds },
  } = useGraphField<string[], HTMLElement>({ name: 'hiddenEmbeds' });

  const onChange = useCallback(
    async (newValue?: SerializedValue) => {
      if (newValue == null) {
        // Draftjs case
        if (!editorRef?.current) return;

        newValue = (await editorRef.current.getData())?.content;
      }
      form.change(name, newValue);
      serializeValue(newValue);
    },
    [editorRef, form, name, serializeValue],
  );

  // TODO needs to be typed properly
  const onFocusComputed = useCallback(
    (e: any) => {
      onFocus?.(e);
      propsOnFocus?.(e);
    },
    [onFocus, propsOnFocus],
  );

  const onBlurComputed = useCallback(
    (e: any) => {
      onBlur?.(e);
      propsOnBlur?.(e);
    },
    [onBlur, propsOnBlur],
  );

  return (
    <>
      {!frontConfig.enableExperimentalEditorjs ? (
        <WrappedEditor
          key={id}
          forwardedRef={editorRef}
          className={classnames(
            'flex-1 flex flex-col-reverse',
            computedError && 'bg-gray-light rounded-sm overflow-hidden w-full',
          )}
          initialContent={serializedToFullHTML(content) || DEFAULT_EDITOR_CONTENT}
          onChange={onChange}
          skipEmbeds={getValidHiddenEmbeds(hiddenEmbeds)}
          onFocus={onFocusComputed}
          onBlur={onBlurComputed}
          website={website}
          {...props}
        />
      ) : (
        <div className="flex flex-col flex-1 min-w-0">
          {/* <div className="flex gap-1 items-start">
              <Preview name={name} />
            </div> */}
          <Editorjs
            key={id}
            forwardedRef={editorRef}
            initialContent={content}
            onChange={onChange}
            onFocus={onFocusComputed}
            onBlur={onBlurComputed}
            autofocus={props.autofocus}
            hasExternalSend={hasExternalSend}
            onCancelAnswer={onCancelAnswer}
            loading={props.loading}
            hasInitialValue={hasInitialValue}
            hideIfEmpty={hideIfEmpty}
            noBorder={noBorder}
            withAvatar={withAvatar}
            contentType={contentType}
          />
        </div>
      )}

      {computedError ? (
        <p className="text-primary-base border-none mb-3 text-xs ">
          <FormattedMessage {...(computedError as MessageDescriptor)} />
        </p>
      ) : null}
    </>
  );
};

// In case we want a live preview (it was useful for the development)
// interface PreviewProps {
//   name: string;
// }
//
// const Preview: FC<PreviewProps> = memo(function Preview(props) {
//   const { name } = props;
//   const field = useField<SerializedValue>(name);
//   return (
//     <div className="flex-1">
//       <div className="h-[42px]">
//         <i>Preview</i>
//       </div>
//       <RawMessageContent className="" content={field.input.value} withMedia {...props} />
//     </div>
//   );
// });
